import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { MultiSelect } from '../components/ui/MultiSelect';
import { Textarea } from '../components/ui/Textarea';
import { Plus, Filter, Download, Eye, Edit, Search, PhoneCall, User, Upload, Trash2, Ban, AlertTriangle, Save, CheckCircle2, X } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
// Mock data
const initialLeadsData = Array.from({
  length: 50
}, (_, i) => ({
  id: String(i + 1),
  name: `Azienda Lead ${i} SPA`,
  contact: `Referente ${i}`,
  phone: `333 ${1000000 + i}`,
  email: `info@lead${i}.it`,
  status: i % 5 === 0 ? 'Positivo' : i % 5 === 1 ? 'Negativo' : i % 5 === 2 ? 'In lavorazione' : i % 5 === 3 ? 'Da ricontattare' : 'Da contattare',
  source: i % 4 === 0 ? 'Fiera' : i % 4 === 1 ? 'Web' : i % 4 === 2 ? 'Passaparola' : 'Altro',
  services: ['Consulenza'],
  notes: 'Interessati a sicurezza',
  lastContact: '2024-05-10',
  assignedTo: i % 3 === 0 ? 'Alessio' : i % 3 === 1 ? 'Lorenzo' : 'Gian Franco',
  referent: `Referente Interno ${i % 4}`
}));
const blacklistData = [{
  id: '1',
  name: 'Spam Company SRL',
  phone: '0000000000',
  reason: 'Non interessato',
  date: '2024-01-01',
  by: 'Admin'
}];
const columnsConfig = [{
  key: 'name',
  label: 'Ragione sociale'
}, {
  key: 'contact',
  label: 'Referente'
}, {
  key: 'phone',
  label: 'Telefono'
}, {
  key: 'source',
  label: 'Provenienza'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'lastContact',
  label: 'Ultimo contatto'
}, {
  key: 'assignedTo',
  label: 'Assegnato a'
}, {
  key: 'actions',
  label: 'Azioni'
}];
const kanbanColumns = [{
  id: 'Da contattare',
  title: 'Da contattare',
  color: 'bg-slate-50 border-slate-200'
}, {
  id: 'Da ricontattare',
  title: 'Da ricontattare',
  color: 'bg-yellow-50 border-yellow-200'
}, {
  id: 'In lavorazione',
  title: 'In lavorazione',
  color: 'bg-blue-50 border-blue-200'
}, {
  id: 'Positivo',
  title: 'Positivo',
  color: 'bg-green-50 border-green-200'
}, {
  id: 'Negativo',
  title: 'Negativo',
  color: 'bg-red-50 border-red-200'
}];
export function CommercialPage() {
  const [viewMode, setViewMode] = useState<'list' | 'kanban' | 'blacklist'>('kanban');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isBlacklistModalOpen, setIsBlacklistModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [leadsData, setLeadsData] = useState(initialLeadsData);
  const [draggedCard, setDraggedCard] = useState<any>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const {
    addNotification
  } = useNotifications();
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(leadsData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['name', 'contact', 'phone']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({
      status: 'Da contattare',
      services: []
    });
    setIsModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setSelectedItem(item);
    setIsBlacklistModalOpen(true);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
    }, 1500);
  };
  const confirmBlacklist = () => {
    addNotification('success', 'Lead eliminato', `Lead ${selectedItem.name} aggiunto alla blacklist.`);
    setIsBlacklistModalOpen(false);
  };
  const handleUpload = () => {
    setTimeout(() => {
      addNotification('success', 'Importazione completata', '5 lead importati con successo. 1 duplicato ignorato.');
      setIsUploadModalOpen(false);
    }, 1500);
  };
  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, card: any) => {
    setDraggedCard(card);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    if (draggedCard && draggedCard.status !== targetStatus) {
      setLeadsData(prev => prev.map(item => item.id === draggedCard.id ? {
        ...item,
        status: targetStatus
      } : item));
      addNotification('info', 'Stato aggiornato', `Lead spostato in "${targetStatus}"`);
    }
    setDraggedCard(null);
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  return <Layout data-id="element-1017">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-1018">
        <div data-id="element-1019">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-1020">Commerciale</h1>
          <p className="text-slate-500 mt-1" data-id="element-1021">
            Gestione pipeline, lead e contatti.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-1022">
          <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-1023">
            <button onClick={() => setViewMode('kanban')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'kanban' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1024">
              Pipeline
            </button>
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1025">
              Lista
            </button>
            <button onClick={() => setViewMode('blacklist')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'blacklist' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1026">
              Blacklist
            </button>
          </div>
          <Button variant="flat" color="secondary" onClick={() => setIsUploadModalOpen(true)} startContent={<Upload className="h-4 w-4" data-id="element-1028" />} data-id="element-1027">
            Importa
          </Button>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-1030" />} data-id="element-1029">
            Nuovo lead
          </Button>
        </div>
      </div>

      {viewMode !== 'blacklist' && <Card className="mb-6" noPadding data-id="element-1031">
          <div className="p-4 flex flex-col gap-4" data-id="element-1032">
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-1033">
              <div className="flex-1" data-id="element-1034">
                <Input placeholder="Cerca lead..." startContent={<Search className="h-4 w-4" data-id="element-1036" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-1035" />
              </div>
              <div className="flex gap-2" data-id="element-1037">
                <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-1039" />} data-id="element-1038">
                  Filtri{' '}
                  {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-1040">
                      {Object.keys(filters).filter(k => filters[k]).length}
                    </Badge>}
                </Button>
                {viewMode === 'list' && <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="commercial" data-id="element-1041" />}
              </div>
            </div>

            {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-1042">
                <Select label="Provenienza" value={filters.source || ''} onChange={e => setFilter('source', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, {
            value: 'Web',
            label: 'Web'
          }, {
            value: 'Fiera',
            label: 'Fiera'
          }, {
            value: 'Passaparola',
            label: 'Passaparola'
          }, {
            value: 'Altro',
            label: 'Altro'
          }]} data-id="element-1043" />
                <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Da contattare',
            label: 'Da contattare'
          }, {
            value: 'Da ricontattare',
            label: 'Da ricontattare'
          }, {
            value: 'In lavorazione',
            label: 'In lavorazione'
          }, {
            value: 'Positivo',
            label: 'Positivo'
          }, {
            value: 'Negativo',
            label: 'Negativo'
          }]} data-id="element-1044" />
                <Select label="Servizi" value={filters.services || ''} onChange={e => setFilter('services', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Consulenza',
            label: 'Consulenza'
          }, {
            value: 'Formazione',
            label: 'Formazione'
          }, {
            value: 'HACCP',
            label: 'HACCP'
          }, {
            value: 'Privacy',
            label: 'Privacy'
          }]} data-id="element-1045" />
                <Select label="Assegnato a" value={filters.assignedTo || ''} onChange={e => setFilter('assignedTo', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Alessio',
            label: 'Alessio'
          }, {
            value: 'Lorenzo',
            label: 'Lorenzo'
          }, {
            value: 'Gian Franco',
            label: 'Gian Franco'
          }, {
            value: 'Tiziano',
            label: 'Tiziano'
          }]} data-id="element-1046" />
                <div className="col-span-full flex justify-end" data-id="element-1047">
                  <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-1049" />} data-id="element-1048">
                    Resetta filtri
                  </Button>
                </div>
              </div>}
          </div>
        </Card>}

      {viewMode === 'kanban' && <div className="overflow-x-auto pb-4" data-id="element-1050">
          <div className="flex gap-4 min-w-max" data-id="element-1051">
            {kanbanColumns.map(col => {
          const cards = searchResult.filter(i => i.status === col.id);
          return <div key={col.id} className={`w-72 flex-shrink-0 rounded-xl border ${col.color} flex flex-col max-h-[calc(100vh-280px)]`} onDragOver={handleDragOver} onDrop={e => handleDrop(e, col.id)} data-id="element-1052">
                  <div className="p-3 border-b border-slate-200/50 flex items-center justify-between" data-id="element-1053">
                    <h3 className="font-semibold text-slate-700 text-sm" data-id="element-1054">
                      {col.title}
                    </h3>
                    <Badge variant="neutral" size="sm" data-id="element-1055">
                      {cards.length}
                    </Badge>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-2" data-id="element-1056">
                    {cards.map(item => <div key={item.id} draggable onDragStart={e => handleDragStart(e, item)} onClick={() => handleView(item)} className={`bg-white p-3 rounded-lg border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer ${draggedCard?.id === item.id ? 'opacity-50' : ''}`} data-id="element-1057">
                        <div className="flex justify-between items-start mb-2" data-id="element-1058">
                          <span className="font-bold text-seform-navy text-sm line-clamp-1" data-id="element-1059">
                            {item.name}
                          </span>
                          <Badge variant="neutral" size="sm" className="text-[10px] flex-shrink-0 ml-2" data-id="element-1060">
                            {item.source}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mb-1 flex items-center gap-1" data-id="element-1061">
                          <User className="h-3 w-3" data-id="element-1062" /> {item.contact}
                        </p>
                        <p className="text-xs text-slate-500 mb-2 flex items-center gap-1" data-id="element-1063">
                          <PhoneCall className="h-3 w-3" data-id="element-1064" /> {item.phone}
                        </p>
                        <div className="flex justify-between items-center pt-2 border-t border-slate-50" data-id="element-1065">
                          <span className="text-[10px] text-slate-400" data-id="element-1066">
                            {item.lastContact}
                          </span>
                          <div className="w-6 h-6 rounded-full bg-seform-orange/10 text-seform-orange text-xs flex items-center justify-center font-bold" data-id="element-1067">
                            {item.assignedTo.charAt(0)}
                          </div>
                        </div>
                      </div>)}
                    {cards.length === 0 && <div className="text-center py-8 text-slate-400 text-sm" data-id="element-1068">
                        Nessun elemento
                      </div>}
                  </div>
                </div>;
        })}
          </div>
        </div>}

      {viewMode === 'list' && <Card noPadding className="overflow-hidden" data-id="element-1069">
          <div className="overflow-x-auto" data-id="element-1070">
            <table className="w-full text-left border-collapse table-fixed" data-id="element-1071">
              <thead data-id="element-1072">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1073">
                  {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-6 py-4 ${col.key === 'actions' ? 'text-right w-28' : ''}`} data-id="element-1074">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1075">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-1076">
                    {visibleColumns.includes('name') && <td className="px-6 py-4 font-medium text-slate-900" data-id="element-1077">
                        {item.name}
                      </td>}
                    {visibleColumns.includes('contact') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-1078">
                        {item.contact}
                      </td>}
                    {visibleColumns.includes('phone') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-1079">
                        {item.phone}
                      </td>}
                    {visibleColumns.includes('source') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-1080">
                        {item.source}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-6 py-4" data-id="element-1081">
                        <Badge variant={item.status === 'Positivo' ? 'success' : item.status === 'Negativo' ? 'danger' : 'info'} data-id="element-1082">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('lastContact') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-1083">
                        {item.lastContact}
                      </td>}
                    {visibleColumns.includes('assignedTo') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-1084">
                        {item.assignedTo}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-6 py-4 text-right" data-id="element-1085">
                        <div className="flex items-center justify-end gap-2" data-id="element-1086">
                          <Button variant="light" size="sm" className="!px-2" onClick={() => handleView(item)} data-id="element-1087">
                            <Eye className="h-4 w-4 text-slate-500" data-id="element-1088" />
                          </Button>
                          <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(item)} data-id="element-1089">
                            <Edit className="h-4 w-4 text-seform-orange" data-id="element-1090" />
                          </Button>
                          <Button variant="light" size="sm" className="!px-2" onClick={() => handleDelete(item)} data-id="element-1091">
                            <Trash2 className="h-4 w-4 text-red-500" data-id="element-1092" />
                          </Button>
                        </div>
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-1093">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-1094" />
          </div>
        </Card>}

      {viewMode === 'blacklist' && <Card noPadding className="overflow-hidden" data-id="element-1095">
          <div className="p-4 bg-red-50 border-b border-red-100 flex items-center gap-2 text-red-700 text-sm" data-id="element-1096">
            <Ban className="h-4 w-4" data-id="element-1097" />I contatti in questa lista sono bloccati
            e non possono essere re-importati o creati manualmente.
          </div>
          <div className="overflow-x-auto" data-id="element-1098">
            <table className="w-full text-left border-collapse" data-id="element-1099">
              <thead data-id="element-1100">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1101">
                  <th className="px-6 py-4" data-id="element-1102">Ragione sociale</th>
                  <th className="px-6 py-4" data-id="element-1103">Telefono</th>
                  <th className="px-6 py-4" data-id="element-1104">Motivazione</th>
                  <th className="px-6 py-4" data-id="element-1105">Data eliminazione</th>
                  <th className="px-6 py-4" data-id="element-1106">Eliminato da</th>
                  <th className="px-6 py-4 text-right" data-id="element-1107">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1108">
                {blacklistData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors" data-id="element-1109">
                    <td className="px-6 py-4 font-medium text-slate-900" data-id="element-1110">
                      {item.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-1111">
                      {item.phone}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-1112">
                      {item.reason}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-1113">
                      {item.date}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-1114">
                      {item.by}
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-1115">
                      <Button variant="light" color="success" size="sm" data-id="element-1116">
                        Ripristina
                      </Button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Card>}

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Dettaglio lead" size="lg" data-id="element-1117">
        {selectedItem && <div className="space-y-6" data-id="element-1118">
            <div className="grid grid-cols-2 gap-4" data-id="element-1119">
              <div data-id="element-1120">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1121">
                  Ragione sociale
                </span>
                <p className="font-medium" data-id="element-1122">{selectedItem.name}</p>
              </div>
              <div data-id="element-1123">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1124">
                  Stato
                </span>
                <div className="mt-1" data-id="element-1125">
                  <Badge variant={selectedItem.status === 'Positivo' ? 'success' : selectedItem.status === 'Negativo' ? 'danger' : 'info'} data-id="element-1126">
                    {selectedItem.status}
                  </Badge>
                </div>
              </div>
              <div data-id="element-1127">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1128">
                  Referente
                </span>
                <p className="font-medium" data-id="element-1129">{selectedItem.contact}</p>
              </div>
              <div data-id="element-1130">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1131">
                  Telefono
                </span>
                <p className="font-medium" data-id="element-1132">{selectedItem.phone}</p>
              </div>
              <div data-id="element-1133">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1134">
                  Email
                </span>
                <p className="font-medium" data-id="element-1135">{selectedItem.email}</p>
              </div>
              <div data-id="element-1136">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1137">
                  Provenienza
                </span>
                <p className="font-medium" data-id="element-1138">{selectedItem.source}</p>
              </div>
              <div data-id="element-1139">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1140">
                  Assegnato a
                </span>
                <p className="font-medium" data-id="element-1141">{selectedItem.assignedTo}</p>
              </div>
              <div data-id="element-1142">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1143">
                  Ultimo contatto
                </span>
                <p className="font-medium" data-id="element-1144">{selectedItem.lastContact}</p>
              </div>
            </div>
            {selectedItem.notes && <div data-id="element-1145">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1146">
                  Note
                </span>
                <p className="text-sm text-slate-600 mt-1" data-id="element-1147">
                  {selectedItem.notes}
                </p>
              </div>}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1148">
              <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-1149">
                Chiudi
              </Button>
              <Button onClick={() => {
            setIsViewModalOpen(false);
            handleEdit(selectedItem);
          }} startContent={<Edit className="h-4 w-4" data-id="element-1151" />} data-id="element-1150">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica lead' : 'Nuovo lead'} size="2xl" data-id="element-1152">
        <AnimatePresence mode="wait" data-id="element-1153">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-1154">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-1155">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-1156" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-1157">
                Lead salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-1158">
              <div className="space-y-8" data-id="element-1159">
                <section data-id="element-1160">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1161">
                    <User className="h-4 w-4" data-id="element-1162" /> Dati lead
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1163">
                    <Input label="Ragione sociale" placeholder="Azienda SPA" value={formData.name || ''} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} required data-id="element-1164" />
                    <Input label="Referente" placeholder="Nome Cognome" value={formData.contact || ''} onChange={e => setFormData({
                  ...formData,
                  contact: e.target.value
                })} required data-id="element-1165" />
                    <Input label="Telefono" type="tel" value={formData.phone || ''} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} required data-id="element-1166" />
                    <Input label="Email" type="email" value={formData.email || ''} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} data-id="element-1167" />
                    <Select label="Provenienza" value={formData.source || ''} onChange={e => setFormData({
                  ...formData,
                  source: e.target.value
                })} options={[{
                  value: 'Web',
                  label: 'Web'
                }, {
                  value: 'Fiera',
                  label: 'Fiera'
                }, {
                  value: 'Passaparola',
                  label: 'Passaparola'
                }, {
                  value: 'Altro',
                  label: 'Altro'
                }]} required data-id="element-1168" />
                    <Select label="Stato" value={formData.status || 'Da contattare'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Da contattare',
                  label: 'Da contattare'
                }, {
                  value: 'Da ricontattare',
                  label: 'Da ricontattare'
                }, {
                  value: 'In lavorazione',
                  label: 'In lavorazione'
                }, {
                  value: 'Positivo',
                  label: 'Positivo'
                }, {
                  value: 'Negativo',
                  label: 'Negativo'
                }]} required data-id="element-1169" />
                    <Select label="Assegnato a" value={formData.assignedTo || ''} onChange={e => setFormData({
                  ...formData,
                  assignedTo: e.target.value
                })} options={[{
                  value: 'Alessio',
                  label: 'Alessio'
                }, {
                  value: 'Lorenzo',
                  label: 'Lorenzo'
                }, {
                  value: 'Gian Franco',
                  label: 'Gian Franco'
                }, {
                  value: 'Tiziano',
                  label: 'Tiziano'
                }]} required data-id="element-1170" />
                    <div className="col-span-full" data-id="element-1171">
                      <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1172">
                        Servizi di interesse
                      </label>
                      <div className="grid grid-cols-2 gap-3" data-id="element-1173">
                        {[{
                      value: 'consulenza',
                      label: 'Consulenza'
                    }, {
                      value: 'formazione',
                      label: 'Formazione'
                    }, {
                      value: 'haccp',
                      label: 'HACCP'
                    }, {
                      value: 'privacy',
                      label: 'Privacy'
                    }].map(service => <label key={service.value} className="flex items-center gap-2 cursor-pointer" data-id="element-1174">
                            <input type="checkbox" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={(formData.services || []).includes(service.value)} onChange={e => {
                        const current = formData.services || [];
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            services: [...current, service.value]
                          });
                        } else {
                          setFormData({
                            ...formData,
                            services: current.filter((s: string) => s !== service.value)
                          });
                        }
                      }} data-id="element-1175" />
                            <span className="text-sm text-slate-700" data-id="element-1176">
                              {service.label}
                            </span>
                          </label>)}
                      </div>
                    </div>
                    <div className="col-span-full" data-id="element-1177">
                      <Textarea label="Note" value={formData.notes || ''} onChange={e => setFormData({
                    ...formData,
                    notes: e.target.value
                  })} data-id="element-1178" />
                    </div>
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1179">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-1180">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-1182" />} data-id="element-1181">
                    Salva lead
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Upload Modal */}
      <Modal isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} title="Importa lead da Excel" size="lg" data-id="element-1183">
        <div className="space-y-6" data-id="element-1184">
          <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:bg-slate-50 transition-colors cursor-pointer" data-id="element-1185">
            <Upload className="h-10 w-10 text-slate-400 mx-auto mb-4" data-id="element-1186" />
            <p className="text-sm text-slate-600 font-medium" data-id="element-1187">
              Trascina qui il file Excel o clicca per selezionare
            </p>
            <p className="text-xs text-slate-400 mt-2" data-id="element-1188">
              Formati supportati: .xlsx, .csv
            </p>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-1189">
            <h5 className="text-sm font-bold text-blue-800 mb-2" data-id="element-1190">
              Campi richiesti nel file:
            </h5>
            <ul className="list-disc list-inside text-xs text-blue-700 space-y-1" data-id="element-1191">
              <li data-id="element-1192">Ragione sociale</li>
              <li data-id="element-1193">Telefono</li>
              <li data-id="element-1194">Referente</li>
              <li data-id="element-1195">Provenienza</li>
            </ul>
            <div className="mt-3 text-xs text-blue-600" data-id="element-1196">
              <a href="#" className="underline" data-id="element-1197">
                Scarica template di esempio
              </a>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4" data-id="element-1198">
            <Button variant="flat" color="default" onClick={() => setIsUploadModalOpen(false)} data-id="element-1199">
              Annulla
            </Button>
            <Button onClick={handleUpload} data-id="element-1200">Importa file</Button>
          </div>
        </div>
      </Modal>

      {/* Blacklist Modal */}
      <Modal isOpen={isBlacklistModalOpen} onClose={() => setIsBlacklistModalOpen(false)} title="Elimina e aggiungi a blacklist" size="md" data-id="element-1201">
        <div className="space-y-6" data-id="element-1202">
          <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-1203">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-1204" />
            <p className="text-sm text-red-700" data-id="element-1205">
              Attenzione: il lead verrà rimosso dalla pipeline e aggiunto alla
              blacklist. Non sarà più possibile importarlo o crearlo manualmente
              con questo numero di telefono.
            </p>
          </div>

          <Select label="Motivazione eliminazione" options={[{
          value: 'not_interested',
          label: 'Non interessato'
        }, {
          value: 'no_answer',
          label: 'Non ha mai risposto'
        }, {
          value: 'unreachable',
          label: 'Impossibile parlare con referente'
        }, {
          value: 'other',
          label: 'Altro'
        }]} required data-id="element-1206" />

          <Textarea label="Note aggiuntive" placeholder="Dettagli..." data-id="element-1207" />

          <div className="flex justify-end gap-3 pt-4" data-id="element-1208">
            <Button variant="flat" color="default" onClick={() => setIsBlacklistModalOpen(false)} data-id="element-1209">
              Annulla
            </Button>
            <Button color="danger" onClick={confirmBlacklist} data-id="element-1210">
              Conferma eliminazione
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>;
}